import { Box } from '@coolblue-development/freddy';
import showAccessoryProductsTrigger from '@coolblue-development/ga4-events/triggers/ProductTypeSection/showAccessoryProductsTrigger';
import viewTrigger from '@coolblue-development/ga4-events/triggers/ProductTypeSection/viewTrigger';
import { TrackImmediate, TrackWhenVisible } from '@coolblue-development/next-analytics/client';
import { ProductCardSectionContent } from '@features/add-to-cart-page/components/Sections/ProductCardSectionContent';
import SectionTitle from '@features/add-to-cart-page/components/SectionTitle/SectionTitle';
import { ATCPageProductTypeSection } from '@features/add-to-cart-page/typings/ProductTypeSection';
import { getAddToCartPageType } from '@features/add-to-cart-page/utils/getAddToCartPageType';
import useTestId from '@hooks/useTestId';
import { useTranslation } from '@i18n/useTranslation';
import React from 'react';

import { AddToCartPageSectionProps } from '../../typings/AddToCartPageSection';
import { useCustomerContext } from './CustomerContext/CustomerContext';

export function ProductTypeSection({
  product,
  section,
  order,
  shouldUsePromotionalContent
}: AddToCartPageSectionProps) {
  const { t } = useTranslation('webshop.addToCart.productTypeSection');
  const testId = useTestId('product-type-section');
  const { isBusinessCustomer } = useCustomerContext();
  const addToCartPageType = getAddToCartPageType({ isBusiness: isBusinessCustomer });

  const productTypeSection = section as ATCPageProductTypeSection;

  const productTypeName = product.productType?.singularName || undefined;

  const title = productTypeSection.title
    ? productTypeSection.title
    : productTypeName
      ? t('defaultTitle', { productTypeName })
      : undefined;

  const attachedItemIds = productTypeSection.slots
    .map(slot => slot.slotProduct.id.toString())
    .join(',');

  const showAccessoryProductsParams: {
    primary_item_id: number;
    attached_item_ids: string;
    sub_feature: string | null;
  } = {
    primary_item_id: product.id,
    attached_item_ids: attachedItemIds,
    sub_feature: addToCartPageType
  };

  const viewAccessoryProductsParams: Parameters<typeof viewTrigger>[0] = {
    item_ids: attachedItemIds,
    has_highlighted_segment: !!productTypeSection.slots.find(slot => !!slot.highlightedAlternative)
  };

  return (
    <Box {...testId} mt={800} component='section'>
      <TrackImmediate trigger={showAccessoryProductsTrigger(showAccessoryProductsParams)} />
      {title && <SectionTitle title={title} index={order} />}
      <TrackWhenVisible trigger={viewTrigger(viewAccessoryProductsParams)}>
        <ProductCardSectionContent
          section={productTypeSection}
          mainProductId={product.id}
          shouldUsePromotionalContent={shouldUsePromotionalContent}
        />
      </TrackWhenVisible>
    </Box>
  );
}
